import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../hooks/useAuth";
import { createEnquiry } from "../services/enquiryService";
import "../style/contact_us.css";
import qaCoderLogo from "../assets/qacoder-logo.svg";

const initialFormData = {
  name: "",
  email: "",
  enquiryType: "contact",
  subject: "",
  phone: "",
  message: "",
};

export default function ContactUs() {
  const { user } = useAuth();
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    ...initialFormData,
    name: user?.displayName || "",
    email: user?.email || "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error("Please fill your name, email, and message.");
      return;
    }

    setLoading(true);
    setStatus("Sending your message...");

    try {
      await createEnquiry(
        {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        enquiryType: formData.enquiryType,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        userId: user?.uid || null,
        },
        {
          source: "contact-page",
          enquiryType: formData.enquiryType,
        }
      );

      setStatus("Message sent successfully. Lead status: new. We will get back to you soon.");
      toast.success("Message sent successfully");
      setFormData({
        ...initialFormData,
        name: user?.displayName || "",
        email: user?.email || "",
      });
    } catch (error) {
      console.error(error);
      setStatus("Unable to send your message right now. Please try again.");
      toast.error(error.message || "Unable to send your message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <main className="contact-shell">
        <section className="contact-hero">
          <div className="contact-hero-copy">
            <span className="contact-chip">Contact QA Coder</span>
            <h1>Talk to us about training, mentorship, or QA guidance.</h1>
            <p>
              Reach out for course details, demo classes, learning support, or collaboration.
              The form below now submits directly through the app so your message is stored
              safely and we can follow up quickly.
            </p>

            <div className="contact-highlights">
              <span>Course guidance</span>
              <span>Career support</span>
              <span>Lead workflow tracking</span>
            </div>
          </div>

          <div className="contact-hero-card">
            <div className="contact-stat-card">
              <strong>2 hrs</strong>
              <span>Typical response time</span>
            </div>
            <div className="contact-stat-card">
              <strong>500+</strong>
              <span>Learners supported</span>
            </div>
            <div className="contact-stat-card">
              <strong>Manual + Automation</strong>
              <span>Guidance across the full QA path</span>
            </div>
          </div>
        </section>

        <section className="contact-main-grid">
          <section className="contact-info-card">
            <div className="contact-brand-visual" aria-label="QA Coder support visual">
              <img src={qaCoderLogo} alt="QA Coder logo" />
              <div className="contact-brand-badges">
                <span>Demo Class</span>
                <span>Course Guidance</span>
                <span>QA Support</span>
              </div>
            </div>

            <h2>Connect with QA Coder Support</h2>
            <p>
              Get practical help for course selection, demo booking, QA career guidance,
              interview preparation, and learning support. Your message is saved as a
              new lead so our team can follow up with the right next step.
            </p>

            <div className="contact-quick-points">
              <div>
                <i className="fa-solid fa-envelope"></i>
                <span>qacoder0@gmail.com</span>
              </div>
              <div>
                <i className="fa-solid fa-location-dot"></i>
                <span>Online and offline QA coaching support</span>
              </div>
            </div>

            <div className="social-links">
              <a href="https://www.linkedin.com/in/qa-coder-708418404/" target="_blank" rel="noopener noreferrer" className="social-icon linkedin">
                <i className="fab fa-linkedin"></i>
                <span>LinkedIn</span>
              </a>
              <a href="https://www.instagram.com/qacoder0/?hl=en" target="_blank" rel="noopener noreferrer" className="social-icon instagram">
                <i className="fab fa-instagram"></i>
                <span>Instagram</span>
              </a>
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon facebook">
                <i className="fab fa-facebook"></i>
                <span>Facebook</span>
              </a>
              <a href="https://www.youtube.com/@QACoder" target="_blank" rel="noopener noreferrer" className="social-icon youtube">
                <i className="fab fa-youtube"></i>
                <span>YouTube</span>
              </a>
              <a href="mailto:qacoder0@gmail.com" className="social-icon email">
                <i className="fas fa-envelope"></i>
                <span>Email</span>
              </a>
            </div>
          </section>

          <section className="contact-form-card">
            <div className="contact-form-head">
              <h2>Send a Message</h2>
              <p>Share your requirement and we will tag it into the right lead workflow.</p>
            </div>

            <form id="contactForm" onSubmit={handleSubmit} className="contact-form-grid">
              <div className="form-group">
                <label htmlFor="name">
                  <i className="fa-solid fa-user"></i> Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <i className="fa-solid fa-envelope"></i> Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">
                  <i className="fa-solid fa-phone"></i> Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                />
              </div>

              <div className="form-group">
                <label htmlFor="enquiryType">
                  <i className="fa-solid fa-tags"></i> Enquiry Type
                </label>
                <select
                  id="enquiryType"
                  name="enquiryType"
                  value={formData.enquiryType}
                  onChange={handleChange}
                >
                  <option value="contact">General Contact</option>
                  <option value="demo">Free Demo Class</option>
                  <option value="course">Course Enquiry</option>
                  <option value="support">Support</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subject">
                  <i className="fa-solid fa-book-open"></i> Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Course enquiry, demo class, mentorship..."
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="message">
                  <i className="fa-solid fa-message"></i> Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us what you are looking for..."
                  required
                ></textarea>
              </div>

              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? "Sending..." : "Send Message"}
              </button>

              <div className="contact-action-row">
                <NavLink to="/enroll">Book demo through form</NavLink>
                <a href="mailto:qacoder0@gmail.com">Email support</a>
              </div>
            </form>

            <p className={`status-message ${status ? "is-visible" : ""}`}>{status}</p>
          </section>
        </section>
      </main>
    </div>
  );
}
